'use strict';

/**
 * stripeTerminal.js
 *
 * Bridge para Stripe Terminal (lectores físicos de tarjeta).
 * Usa @capacitor-community/stripe-terminal v8.
 *
 * Flujo de pago:
 *   1. stripeTerminal.initialize()          — solo, la primera vez que se busca un
 *                                             lector o se cobra con él
 *   2. stripeTerminal.discoverReaders(...)  — busca lectores disponibles
 *   3. stripeTerminal.connectReader(...)    — conecta al lector elegido
 *   4. stripeTerminal.collectPayment(...)   — cobra la tarjeta
 *   5. stripeTerminal.disconnect()          — al cerrar sesión
 *
 * En web/dev todas las operaciones son no-op o lanzan errores descriptivos.
 */

import { StripeTerminal } from '@capacitor-community/stripe-terminal';
import { Capacitor }       from '@capacitor/core';
import { getConnectionToken } from '../api/pos.api.js';

const isNative = Capacitor.isNativePlatform();

// Mapeo de connection_type del PosTerminal → TerminalConnectTypes del plugin
const CONNECTION_TYPE_MAP = {
  bluetooth:    'bluetooth',
  internet:     'internet',
  local_mobile: 'tap-to-pay',
};

let _initialized   = false;
let _iniciando     = null;
let _reader        = null;
let _tokenListener = null;

// El plugin pide la ubicación (Android la exige para buscar lectores) y, si se
// la niegan, la vuelve a pedir desde su propio callback, sin fin. Cuando Android
// deja de mostrar el diálogo ("no volver a preguntar") la respuesta es
// instantánea y el ciclo congela la app. Por eso: no se inicializa al abrir la
// app —un restaurante con terminal propia jamás necesita el lector— y, en
// Android, el permiso se pregunta aquí UNA vez antes de dárselo al plugin.
async function permisoUbicacion() {
  if (Capacitor.getPlatform() !== 'android') return;
  let p = await StripeTerminal.checkPermissions().catch(() => null);
  if (p && p.location !== 'granted') {
    p = await StripeTerminal.requestPermissions({ permissions: ['location'] }).catch(() => null);
  }
  if (p && p.location !== 'granted') {
    throw new Error('El lector de tarjetas necesita el permiso de ubicación. Dalo en Ajustes › Apps › Geek Commerce POS › Permisos › Ubicación.');
  }
}

// ─── API pública ──────────────────────────────────────────────────────────────

export const stripeTerminal = {

  /**
   * Inicializa el SDK la primera vez que hace falta (buscar, conectar, cobrar).
   * El SDK dispara RequestedConnectionToken cuando necesita un token;
   * lo obtenemos desde nuestro backend autenticado y se lo pasamos de vuelta.
   */
  async initialize() {
    if (_initialized || !isNative) return;
    if (!_iniciando) {
      _iniciando = this._inicializar().finally(() => { _iniciando = null; });
    }
    return _iniciando;
  },

  async _inicializar() {
    await permisoUbicacion();

    // Listener de token ANTES de initialize para no perder la primera solicitud
    _tokenListener = await StripeTerminal.addListener(
      'terminalRequestedConnectionToken',
      async () => {
        try {
          const data = await getConnectionToken();
          await StripeTerminal.setConnectionToken({ token: data.secret });
        } catch (err) {
          console.error('[StripeTerminal] Error obteniendo connection token:', err);
        }
      }
    );

    await StripeTerminal.initialize({ isTest: import.meta.env.DEV ?? false });
    _initialized = true;
  },

  /**
   * Descubre lectores disponibles para el tipo de conexión del terminal.
   * @param {object} opts
   * @param {string} opts.connectionType  — 'bluetooth' | 'internet' | 'local_mobile'
   * @param {string} [opts.locationId]    — stripe_location_id del PosTerminal
   * @param {number} [opts.timeout]       — segundos de espera (default 15)
   * @returns {Promise<Array>} lista de readers descubiertos
   */
  async discoverReaders({ connectionType, locationId, timeout = 15 }) {
    if (!isNative) return [];
    await this.initialize();
    const type   = CONNECTION_TYPE_MAP[connectionType] ?? connectionType;
    const result = await StripeTerminal.discoverReaders({
      type,
      locationId,
      timeout,
    });
    return result.readers ?? [];
  },

  /**
   * Conecta a un reader específico de la lista retornada por discoverReaders.
   */
  async connectReader({ reader, locationId }) {
    if (!isNative) return;
    await this.initialize();
    await StripeTerminal.connectReader({
      reader,
      locationId,
      autoReconnectOnUnexpectedDisconnect: true,
    });
    _reader = reader;
  },

  /**
   * Cobra una tarjeta usando el lector conectado.
   * Bloquea hasta que el usuario acerca la tarjeta y el pago es confirmado.
   *
   * @param {object} opts
   * @param {string} opts.clientSecret   — client_secret del PaymentIntent (creado en backend)
   * @returns {{ paymentIntentId: string }}
   */
  async collectPayment({ clientSecret }) {
    if (!isNative) throw new Error('El cobro con tarjeta requiere el dispositivo físico');
    await this.initialize();
    await StripeTerminal.collectPaymentMethod({ paymentIntent: clientSecret });
    await StripeTerminal.confirmPaymentIntent();
    // El paymentIntentId está embebido en el clientSecret: "pi_XXX_secret_YYY"
    const paymentIntentId = clientSecret.split('_secret_')[0];
    return { paymentIntentId };
  },

  /** Cancela una colección de pago en curso. */
  async cancelCollect() {
    if (!isNative) return;
    await StripeTerminal.cancelCollectPaymentMethod().catch(() => {});
  },

  /** Desconecta el lector activo. */
  async disconnect() {
    if (!isNative || !_reader) return;
    await StripeTerminal.disconnectReader().catch(() => {});
    _reader = null;
  },

  /**
   * Registra un callback para desconexión inesperada (cable jalado, BLE fuera de rango, etc.)
   */
  onUnexpectedDisconnect(callback) {
    if (!isNative) return;
    StripeTerminal.addListener('terminalUnexpectedReaderDisconnect', () => {
      _reader = null;
      callback();
    });
  },

  isConnected: () => !!_reader,
  getReader:   () => _reader,
};
